import { useState, useEffect } from 'react';
import offlineStorage from '../utils/offlineStorage.js';

export const usePWA = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);

  useEffect(() => {
    // Check if app is installed
    const checkInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone ||
                       document.referrer.includes('android-app://');
      setIsInstalled(standalone);
    };

    checkInstalled();

    // Listen for online/offline events
    const handleOnline = async () => {
      setIsOnline(true);
      
      // Auto-sync when coming back online
      if (!syncInProgress) {
        setSyncInProgress(true);
        try {
          await offlineStorage.syncAllPending();
        } catch (error) {
          console.error('Failed to sync data:', error);
          // Don't throw error to prevent app crash
        } finally {
          setSyncInProgress(false);
        }
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Enhanced service worker update detection with error handling
    if ('serviceWorker' in navigator) {
      // Safe service worker message handler
      const handleSWMessage = (event) => {
        try {
          if (!event.data || typeof event.data !== 'object') {
            console.warn('usePWA: Invalid SW message format:', event.data);
            return;
          }
          
          if (event.data.type === 'SW_UPDATE_AVAILABLE') {
            setUpdateAvailable(true);
          }
        } catch (error) {
          console.warn('usePWA: Error handling SW message:', error);
        }
      };
      
      // Safe controller change handler
      const handleControllerChange = () => {
        try {
          console.log('usePWA: Service worker controller changed');
          setUpdateAvailable(true);
        } catch (error) {
          console.warn('usePWA: Error handling controller change:', error);
        }
      };
      
      // Safe event listener registration
      try {
        navigator.serviceWorker.addEventListener('message', handleSWMessage);
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      } catch (error) {
        console.warn('usePWA: Failed to register SW event listeners:', error);
      }

      // Enhanced update check with better error handling
      const checkForUpdates = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.update();
            console.log('usePWA: Update check completed successfully');
          } else {
            console.log('usePWA: No service worker registration found');
          }
        } catch (error) {
          // Don't log routine network errors that can happen during update checks
          if (error.name === 'NetworkError' || error.name === 'TypeError') {
            console.log('usePWA: Network error during update check (expected when offline)');
          } else {
            console.warn('usePWA: Failed to check for updates:', error);
          }
        }
      };

      // Check for updates every 10 minutes (reduced frequency to avoid errors)
      const updateInterval = setInterval(checkForUpdates, 10 * 60 * 1000);

      return () => {
        try {
          clearInterval(updateInterval);
          navigator.serviceWorker.removeEventListener('message', handleSWMessage);
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        } catch (error) {
          console.warn('usePWA: Error during cleanup:', error);
        }
        
        // Clean up other listeners
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [syncInProgress]);

  // Install the app with enhanced error handling
  const installApp = async () => {
    if (!installPrompt) {
      console.log('usePWA: No install prompt available');
      return false;
    }

    try {
      // Check if prompt is still valid
      if (typeof installPrompt.prompt !== 'function') {
        console.warn('usePWA: Install prompt is no longer valid');
        setInstallPrompt(null);
        return false;
      }
      
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('usePWA: App installation accepted');
        setInstallPrompt(null);
        return true;
      }
      
      console.log('usePWA: App installation declined');
      return false;
    } catch (error) {
      console.warn('usePWA: Failed to install app:', error);
      // Clear the prompt if it's no longer valid
      setInstallPrompt(null);
      return false;
    }
  };

  // Update the app with enhanced error handling
  const updateApp = async () => {
    try {
      // First try to skip waiting for faster updates
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          // Wait a moment for the new SW to take control
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Then reload the page
      window.location.reload();
    } catch (error) {
      console.warn('usePWA: Error during app update, falling back to simple reload:', error);
      window.location.reload();
    }
  };

  // Force update (for stuck PWAs) with enhanced error handling
  const forceUpdate = async () => {
    try {
      console.log('usePWA: Attempting force update...');
      
      // Try to unregister service worker first
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
            console.log('usePWA: Unregistered service worker');
          }
        } catch (swError) {
          console.warn('usePWA: Failed to unregister service worker:', swError);
        }
      }
      
      // Try to clear caches
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          console.log('usePWA: Cleared all caches');
        } catch (cacheError) {
          console.warn('usePWA: Failed to clear caches:', cacheError);
        }
      }
      
      // Try to load and run the force update script
      try {
        const script = document.createElement('script');
        script.src = '/clear-cache.js';
        script.onerror = () => {
          console.warn('usePWA: Failed to load clear-cache.js, using fallback');
          window.location.reload(true);
        };
        document.head.appendChild(script);
        
        // Give it a moment to load then call the force function
        setTimeout(() => {
          try {
            if (window.forceUpdatePWA) {
              window.forceUpdatePWA();
            } else {
              console.log('usePWA: forceUpdatePWA not available, using fallback');
              window.location.reload(true);
            }
          } catch (scriptError) {
            console.warn('usePWA: Error calling forceUpdatePWA:', scriptError);
            window.location.reload(true);
          }
        }, 1000);
      } catch (scriptError) {
        console.warn('usePWA: Failed to load force update script:', scriptError);
        window.location.reload(true);
      }
    } catch (error) {
      console.error('usePWA: Force update failed, using emergency reload:', error);
      window.location.reload(true);
    }
  };

  // Cache data for offline use with enhanced error handling
  const cacheData = async (key, data, type) => {
    try {
      if (!key || data === undefined) {
        console.warn('usePWA: Invalid cache data parameters');
        return false;
      }
      
      await offlineStorage.cacheData(key, data, type);
      return true;
    } catch (error) {
      // Don't crash on cache errors, just log them
      console.warn('usePWA: Failed to cache data:', error);
      return false;
    }
  };

  // Get cached data with enhanced error handling
  const getCachedData = async (key) => {
    try {
      if (!key) {
        console.warn('usePWA: Invalid cache key');
        return null;
      }
      
      return await offlineStorage.getCachedData(key);
    } catch (error) {
      // Don't crash on cache retrieval errors
      console.warn('usePWA: Failed to get cached data:', error);
      return null;
    }
  };

  // Add offline action with enhanced error handling
  const addOfflineAction = async (type, data) => {
    try {
      if (!type || data === undefined) {
        console.warn('usePWA: Invalid offline action parameters');
        return false;
      }
      
      const wasQueued = await offlineStorage.queueForSync({
        type,
        data,
        timestamp: Date.now()
      });
      
      return !wasQueued; // Returns true if synced immediately, false if queued
    } catch (error) {
      console.warn('usePWA: Failed to add offline action:', error);
      return false;
    }
  };

  // Manual sync with enhanced error handling
  const syncPendingActions = async () => {
    if (!isOnline || syncInProgress) {
      console.log('usePWA: Sync skipped - offline or already in progress');
      return false;
    }

    setSyncInProgress(true);
    try {
      const results = await offlineStorage.syncAllPending();
      console.log('usePWA: Sync completed successfully');
      return results;
    } catch (error) {
      console.warn('usePWA: Failed to sync pending actions:', error);
      return false;
    } finally {
      setSyncInProgress(false);
    }
  };

  // Share content (PWA share API) with enhanced error handling
  const shareContent = async (shareData) => {
    try {
      if (!shareData || typeof shareData !== 'object') {
        console.warn('usePWA: Invalid share data');
        return false;
      }
      
      if (navigator.share) {
        try {
          await navigator.share(shareData);
          return true;
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('usePWA: Share cancelled by user');
            return false;
          }
          console.warn('usePWA: Failed to share:', error);
          // Fall through to clipboard fallback
        }
      }
      
      // Fallback to clipboard
      if (navigator.clipboard && shareData.url) {
        try {
          await navigator.clipboard.writeText(shareData.url);
          console.log('usePWA: Content copied to clipboard');
          return true;
        } catch (error) {
          console.warn('usePWA: Failed to copy to clipboard:', error);
        }
      }
      
      return false;
    } catch (error) {
      console.warn('usePWA: Share operation failed:', error);
      return false;
    }
  };

  // Request persistent storage with enhanced error handling
  const requestPersistentStorage = async () => {
    try {
      if ('storage' in navigator && 'persist' in navigator.storage) {
        const granted = await navigator.storage.persist();
        console.log('usePWA: Persistent storage request result:', granted);
        return granted;
      }
      
      console.log('usePWA: Persistent storage not supported');
      return false;
    } catch (error) {
      console.warn('usePWA: Failed to request persistent storage:', error);
      return false;
    }
  };

  // Get storage usage with enhanced error handling
  const getStorageUsage = async () => {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const usage = {
          used: estimate.usage || 0,
          available: estimate.quota || 0,
          percentage: estimate.quota ? (estimate.usage / estimate.quota) * 100 : 0
        };
        console.log('usePWA: Storage usage:', usage);
        return usage;
      }
      
      console.log('usePWA: Storage estimation not supported');
      return null;
    } catch (error) {
      console.warn('usePWA: Failed to get storage usage:', error);
      return null;
    }
  };

  return {
    // Status
    isOnline,
    isInstalled,
    updateAvailable,
    syncInProgress,
    installPrompt: !!installPrompt,
    
    // Actions
    installApp,
    updateApp,
    forceUpdate,
    cacheData,
    getCachedData,
    addOfflineAction,
    syncPendingActions,
    shareContent,
    requestPersistentStorage,
    getStorageUsage
  };
};

export default usePWA;